import { useState } from 'react';
import { idError } from '../../lib/country.ts';
import { CountrySelect } from '../../components/CountrySelect.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { countryName } from '../../lib/business.ts';
import { ratePct, useTaxRates, type CountryRules } from '../../lib/taxes.ts';
import { Link } from 'react-router';
import { usePlaces } from '../../lib/places.tsx';
import type { Place } from '../../lib/types.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

const CODE = /^[A-Z0-9]{2,6}$/;
const UNIT = /^[a-z]{2}\d{3}[a-z]{2}\d{3}$/;

/**
 * The places the business works from. One menu, one team and one set of books; each place has
 * its own tills, floors, kitchen printers and stock, and the reports show each and all together.
 */
export function Locations() {
  const { data, reload } = useAction<Place[]>('company.locations');
  const places = usePlaces();
  const [editing, setEditing] = useState<Place | 'new' | null>(null);
  const refresh = async () => {
    await reload();
    await places.reload();
  };
  const open = data?.filter((l) => l.active) ?? [];
  const closed = data?.filter((l) => !l.active) ?? [];

  return (
    <ManageLayout
      title={t('manage.locations')}
      subtitle={t('locations.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')} disabled={!data}>
          {t('locations.add')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : (
        <>
          {open.length === 1 ? (
            <div className="callout callout-info">
              <Icon name="store" size={18} />
              <span>{t('locations.single_hint')}</span>
            </div>
          ) : null}
          <div className="list-card" role="list">
            {open.map((l) => (
              <PlaceRow key={l.id} place={l} onOpen={() => setEditing(l)} />
            ))}
          </div>
          {closed.length ? (
            <>
              <h2 className="section-title">{t('locations.closed_title')}</h2>
              <div className="list-card" role="list">
                {closed.map((l) => (
                  <PlaceRow key={l.id} place={l} onOpen={() => setEditing(l)} />
                ))}
              </div>
            </>
          ) : null}
          {!data.length ? <EmptyState icon="store" title={t('locations.add')} /> : null}
        </>
      )}
      {editing && data ? (
        <PlaceDialog
          place={editing === 'new' ? null : editing}
          others={data.filter((l) => editing === 'new' || l.id !== editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function PlaceRow({ place: l, onOpen }: { place: Place; onOpen: () => void }) {
  const where = [l.address, l.city].filter(Boolean).join(', ');
  return (
    <div role="listitem" className={`list-row${l.active ? '' : ' archived'}`}>
      <span className="place-badge" aria-hidden="true">
        {l.code}
      </span>
      <button className="list-main" onClick={onOpen}>
        <span className="list-title">{l.name}</span>
        <span className="list-sub">
          <Icon name="pin" size={14} />
          <span>{where || t('locations.no_address')}</span>
          {l.country ? <span>· {[l.region, countryName(l.country)].filter(Boolean).join(', ')}</span> : l.region ? <span>· {l.region}</span> : null}
          {l.business_unit_code ? <span className="mono">· {t('locations.unit_short', { code: l.business_unit_code })}</span> : null}
          {typeof l.sales_tax_rate === 'number' ? <span>· {t('locations.sales_tax_short', { rate: ratePct(l.sales_tax_rate) })}</span> : null}
        </span>
      </button>
      {l.main ? <StatusBadge tone="info" icon="store" label={t('locations.main')} /> : null}
      {l.active ? null : <StatusBadge tone="neutral" icon="pause" label={t('locations.closed')} />}
    </div>
  );
}

function PlaceDialog({ place, others, onClose, onSaved }: { place: Place | null; others: Place[]; onClose: () => void; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(place?.name ?? '');
  const [code, setCode] = useState(place?.code ?? '');
  const [address, setAddress] = useState(place?.address ?? '');
  const [city, setCity] = useState(place?.city ?? '');
  const [postcode, setPostcode] = useState(place?.postcode ?? '');
  const [phone, setPhone] = useState(place?.phone ?? '');
  const [unit, setUnit] = useState(place?.business_unit_code ?? '');
  const home = useTaxRates().rules?.code ?? 'AL';
  const [country, setCountry] = useState(place?.country ?? '');
  const [region, setRegion] = useState(place?.region ?? '');
  const where = country || home;
  const { data: rules } = useAction<CountryRules>('company.country_rules', { country: where });
  const albania = where === 'AL';
  const salesTax = rules?.tax === 'sales_tax';
  const taxes = useTaxRates();
  const [stRate, setStRate] = useState<number | null>(place?.sales_tax_rate ?? null);
  const [tried, setTried] = useState(false);
  const [closing, setClosing] = useState(false);

  const upper = code.trim().toUpperCase();
  const lowerUnit = unit.trim().toLowerCase();
  const errors = {
    name: !name.trim()
      ? t('form.required')
      : others.some((o) => o.name.toLowerCase() === name.trim().toLowerCase())
        ? t('locations.name_taken')
        : null,
    code: !CODE.test(upper) ? t('locations.code_rule') : others.some((o) => o.code === upper) ? t('locations.code_taken', { name: others.find((o) => o.code === upper)!.name }) : null,
    unit: albania && lowerUnit && !UNIT.test(lowerUnit) ? t('locations.unit_rule') : null,
    postcode: rules?.code === where ? idError(rules.address.postcode, postcode, { keepSpaces: true }) : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const body = (active: boolean) => ({
    ...(place ? { id: place.id } : {}),
    name: name.trim(),
    code: upper,
    address,
    city,
    postcode: postcode.trim().toUpperCase(),
    phone,
    business_unit_code: albania ? lowerUnit : '',
    country: where === home ? null : where,
    region: rules?.regions ? region : region.trim(),
    sales_tax_rate: salesTax ? stRate : null,
    active,
  });
  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      await action('company.save_location', body(place?.active ?? true));
      onSaved();
    }, place ? t('locations.saved', { name: name.trim() }) : t('locations.added', { name: name.trim() }));
  };
  const setActive = (active: boolean) =>
    void run(async () => {
      await action('company.save_location', body(active));
      onSaved();
    }, active ? t('locations.reopened', { name: place!.name }) : t('locations.closed_done', { name: place!.name })).finally(() => setClosing(false));

  // Suggest a code from the name while the manager types a new one.
  const onName = (v: string) => {
    setName(v);
    if (!place && (!code || code === suggest(name))) setCode(suggest(v));
  };

  return (
    <Dialog
      title={place ? place.name : t('locations.add')}
      onClose={onClose}
      footer={
        <>
          {place ? (
            place.active ? (
              <Button variant="ghost" icon="pause" className="dialog-left" onClick={() => setClosing(true)} disabled={busy}>
                {t('locations.close')}
              </Button>
            ) : (
              <Button variant="ghost" icon="undo" className="dialog-left" onClick={() => setActive(true)} disabled={busy}>
                {t('locations.reopen')}
              </Button>
            )
          ) : null}
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {place ? t('common.save') : t('locations.add_short')}
          </Button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Field label={t('locations.name')} hint={t('locations.name_hint')} error={tried ? errors.name : null}>
          <input className="input" value={name} maxLength={80} onChange={(e) => onName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('locations.code')} hint={t('locations.code_hint')} error={tried || code ? errors.code : null}>
          <input className="input mono" value={code} maxLength={6} autoCapitalize="characters" onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </Field>
        <Field label={t('business.address')}>
          <input className="input" value={address} maxLength={160} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label={t('business.city')}>
          <input className="input" value={city} maxLength={80} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label={rules?.address.postcode?.label ?? t('business.postcode')} error={tried || postcode ? errors.postcode : null}>
          <input className="input" value={postcode} maxLength={12} autoCapitalize="characters" onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" />
        </Field>
        <Field label={t('business.phone')}>
          <input className="input" type="tel" value={phone} maxLength={40} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label={t('business.country')} hint={t('locations.country_hint')}>
          <CountrySelect
            value={where}
            onChange={(v) => {
              setCountry(v === home ? '' : v);
              setRegion('');
            }}
          />
        </Field>
        {rules?.regions ? (
          <Field label={rules.address.regionLabel ?? t('business.region')} hint={t('locations.region_hint')}>
            <select
              className="input"
              value={region}
              onChange={(e) => {
                setRegion(e.target.value);
                // Where the whole province has one rate (Canada), it is picked for a new location.
                const usual = rules.regions?.find((r) => r.code === e.target.value)?.rate;
                if (salesTax && usual !== undefined && (stRate === null || !place) && taxes.choices().some((r) => Math.abs(r - usual) < 0.001)) setStRate(usual);
              }}
            >
              <option value="">{t('business.region_choose', { name: (rules.address.regionLabel ?? t('business.region')).toLowerCase() })}</option>
              {rules.regions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name} ({r.code})
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {salesTax ? (
          <Field label={t('locations.sales_tax')} hint={t('locations.sales_tax_hint')}>
            <select
              className="input"
              value={stRate === null ? '' : String(stRate)}
              onChange={(e) => setStRate(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">{t('locations.sales_tax_none')}</option>
              {taxes.choices(stRate).map((r) => (
                <option key={r} value={r}>
                  {ratePct(r)} · {taxes.all.find((x) => Math.abs(x.rate - r) < 0.001)?.name ?? ''}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {salesTax && !taxes.all.some((x) => x.active && x.rate > 0) ? (
          <p className="callout callout-warn form-wide">
            <Icon name="alert" size={16} />
            <span>
              {t('locations.sales_tax_add')} <Link to="/manage/taxes">{t('manage.taxes')}</Link>
            </span>
          </p>
        ) : null}
        {albania ? (
          <Field label={t('locations.unit')} hint={t('locations.unit_hint')} error={errors.unit}>
            <input className="input mono" value={unit} maxLength={10} placeholder="ab123cd456" onChange={(e) => setUnit(e.target.value)} />
          </Field>
        ) : null}
        {where !== home ? (
          <p className="callout callout-info form-wide">
            <Icon name="globe" size={16} />
            <span>{t('locations.abroad_note', { country: countryName(where) })}</span>
          </p>
        ) : null}
      </form>
      {closing && place ? (
        <Confirm
          title={t('locations.close_title', { name: place.name })}
          body={t('locations.close_body')}
          confirm={t('locations.close')}
          danger
          busy={busy}
          onConfirm={() => setActive(false)}
          onClose={() => setClosing(false)}
        />
      ) : null}
    </Dialog>
  );
}

function suggest(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 3);
}
